/**
 * Hook personnalisé pour gérer l'installation PWA
 *
 * Ce hook capture l'événement `beforeinstallprompt` du navigateur
 * et fournit une API simple pour déclencher l'installation de la PWA.
 *
 * Fonctionne principalement sur Android/Chrome. Sur iOS, l'installation
 * doit être faite manuellement via le menu Safari.
 *
 * @module useInstallPrompt
 * @author Équipe Brydge
 * @since 1.0.0
 * @returns {Object} État et fonctions pour gérer l'installation
 * @returns {Object|null} returns.deferredPrompt - Événement beforeinstallprompt capturé
 * @returns {string} returns.platform - Plateforme détectée (ios/android/desktop)
 * @returns {boolean} returns.isInstalled - Si l'app est déjà installée
 * @returns {boolean} returns.canShowPrompt - Si le prompt peut être affiché
 * @returns {Function} returns.handleInstall - Fonction pour déclencher l'installation
 */

import {useState, useEffect} from 'react';
import {detectPlatform, isStandalone} from '../utils/detectPlatform';

export const useInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState('desktop');
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Détecter la plateforme
    const detectedPlatform = detectPlatform();
    setPlatform(detectedPlatform);

    // Vérifier si déjà installé
    const installed = isStandalone();
    setIsInstalled(installed);

    // Capturer l'événement beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Capturer l'événement appinstalled
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      localStorage.setItem('pwa_was_installed', 'true');

      // Analytics
      if (window.gtag) {
        window.gtag('event', 'pwa_installed', {
          event_category: 'PWA',
          event_label: platform,
        });
      }
    };

    // Écouter les événements
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [platform]);

  /**
   * Déclenche le prompt d'installation natif
   *
   * Fonctionne uniquement sur Android/Chrome où l'événement
   * beforeinstallprompt a été capturé.
   *
   * @return {Promise<string>} Résultat de l'installation ('accepted', 'dismissed', 'unavailable', 'error')
   */
  const handleInstall = async () => {
    if (!deferredPrompt) {
      return 'unavailable';
    }

    try {
      deferredPrompt.prompt();
      const {outcome} = await deferredPrompt.userChoice;

      // Analytics
      if (window.gtag) {
        window.gtag('event', 'pwa_install_prompt', {
          event_category: 'PWA',
          event_label: outcome,
        });
      }

      setDeferredPrompt(null);
      return outcome;
    } catch (error) {
      console.error('[PWA] Erreur installation:', error);
      return 'error';
    }
  };

  return {
    deferredPrompt,
    platform,
    isInstalled,
    canShowPrompt: Boolean(deferredPrompt) || platform === 'ios',
    handleInstall,
  };
};
